import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import DashboardLayout from "../../components/DashboardLayout";
import OrbitalLoader from "../../components/common/OrbitalLoader";
import ProductionEmptyState from "../../components/common/ProductionEmptyState";
import BrandVideoDetailModal from "../../components/modals/BrandVideoDetailModal";
import GeneratedVideoCard from "../../components/videos/GeneratedVideoCard";
import VideoAccessEmptyState from "../../components/videos/VideoAccessEmptyState";
import { videosService } from "../../services/videos";
import { getTotalPages, parsePaginatedResponse } from "../../utils/pagination";

const PAGE_SIZE = 10;
const VIDEOS_ROUTE_PATTERN = /^\/videos(?:\/([^/]+))?\/?$/;

const parseVideosRoute = (pathname) => {
  const match = pathname.match(VIDEOS_ROUTE_PATTERN);
  return {
    videoHash: match?.[1] ? decodeURIComponent(match[1]) : null,
  };
};

const Videos = ({ currentUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentBrand } = useSelector((state) => state.brand);
  const hasVideos = Boolean(currentUser?.features?.videos);

  const routeState = useMemo(
    () => parseVideosRoute(location.pathname),
    [location.pathname],
  );
  const activeVideoHash = routeState.videoHash;

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [videos, setVideos] = useState([]);
  const [videosCount, setVideosCount] = useState(0);
  const [videosNextPageUrl, setVideosNextPageUrl] = useState(null);
  const [videosPrevPageUrl, setVideosPrevPageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const openVideoDetails = useCallback(
    (videoHash) => {
      if (!videoHash) return;
      navigate({
        pathname: `/videos/${encodeURIComponent(videoHash)}/`,
        search: location.search,
      });
    },
    [location.search, navigate],
  );

  useEffect(() => {
    if (location.pathname === "/videos" || location.pathname === "/videos/") {
      navigate("/videos/", { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 250);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [currentBrand?.hash]);

  useEffect(() => {
    if (!hasVideos || !currentBrand?.hash) {
      setVideos([]);
      setVideosCount(0);
      return;
    }

    setLoading(true);
    setError(null);
    const params = {
      page,
      page_size: PAGE_SIZE,
      brand_hash: currentBrand.hash,
    };
    if (search) params.search = search;

    videosService.listVideos(
      params,
      (response) => {
        const { results, count, next, previous } = parsePaginatedResponse(response);
        setVideos(results);
        setVideosCount(count);
        setVideosNextPageUrl(next);
        setVideosPrevPageUrl(previous);
        setLoading(false);
      },
      () => {
        setError("Could not load videos.");
        setLoading(false);
      },
    );
  }, [currentBrand?.hash, hasVideos, page, search]);

  const activeVideoFromList = useMemo(() => {
    if (!activeVideoHash) return null;
    return videos.find((video) => video.hash === activeVideoHash) || null;
  }, [activeVideoHash, videos]);

  const totalPages = getTotalPages(videosCount, PAGE_SIZE);
  const showPagination = videos.length > 0 && totalPages > 1;
  const showClearFilters = Boolean(search);
  const isTrulyEmpty = !loading && !error && !search && videosCount === 0;

  if (!hasVideos) {
    return (
      <DashboardLayout title="Videos" currentUser={currentUser}>
        <VideoAccessEmptyState />
      </DashboardLayout>
    );
  }

  if (!currentBrand?.hash) {
    return (
      <DashboardLayout title="Videos" currentUser={currentUser}>
        <div className="rounded-lg border border-theme-secondary bg-theme-secondary p-6 text-theme-text opacity-80">
          Select a brand to view videos.
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Videos" currentUser={currentUser}>
      {isTrulyEmpty ? (
        <ProductionEmptyState kind="videos" />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-semibold text-theme-text">
                Videos{" "}
                <span className="text-sm font-normal opacity-50">
                  (
                  {videos.length < videosCount
                    ? `${videos.length}/${videosCount}`
                    : videosCount}
                  )
                </span>
              </h2>
              {showClearFilters ? (
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput("");
                    setSearch("");
                    setPage(1);
                  }}
                  className="text-xs text-theme-text opacity-75 hover:underline"
                >
                  Clear filters
                </button>
              ) : null}
            </div>
            <input
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search videos, scripts, audiences"
              className="h-8 w-56 px-2.5 rounded border border-theme-secondary bg-theme-secondary text-theme-text text-xs"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center min-h-[50vh]">
              <OrbitalLoader size={128} />
            </div>
          ) : error ? (
            <div className="rounded-lg border border-theme-secondary bg-theme-secondary p-4 text-theme-text opacity-80">
              {error}
            </div>
          ) : videos.length === 0 ? (
            <div className="rounded-lg border border-theme-secondary bg-theme-secondary p-6 text-theme-text opacity-80">
              No videos match these filters.
            </div>
          ) : (
            <div className="space-y-3">
              {videos.map((video) => (
                <GeneratedVideoCard
                  key={video.hash}
                  video={video}
                  onOpen={openVideoDetails}
                />
              ))}
            </div>
          )}

          {showPagination ? (
            <div className="flex items-center justify-between pt-1">
              <p className="text-xs text-theme-text opacity-70">
                Page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((value) => Math.max(1, value - 1))}
                  disabled={!videosPrevPageUrl || loading}
                  className="px-3 py-1.5 rounded border border-theme-secondary text-sm text-theme-text hover:bg-theme-main disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setPage((value) => value + 1)}
                  disabled={!videosNextPageUrl || loading}
                  className="px-3 py-1.5 rounded border border-theme-secondary text-sm text-theme-text hover:bg-theme-main disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {activeVideoHash ? (
        <BrandVideoDetailModal
          videoHash={activeVideoHash}
          initialVideo={activeVideoFromList}
          brandHash={currentBrand.hash}
          onClose={() =>
            navigate({ pathname: "/videos/", search: location.search })
          }
        />
      ) : null}
    </DashboardLayout>
  );
};

export default Videos;
